import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, splitRareSenses, useLineFill } from '@shared/lib';
import type { WordSense } from '@shared/types';
import { splitExample } from '../../lib/highlight';
import './SessionSenses.css';

const TILE_VISIBLE = 3;
const PREVIEW_OTHER = 6;
const CONFIRM_MS = 3000;

/** Сколько карточек у слова в этой части речи: «3 карточки» — столько раз оно встретится в тренировке. */
export function SessionPill({ count, className }: { count: number; className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn('ss-pill', className)} title={t('sessionSenses.pillTitle')}>
      {t('sessionSenses.pill', { count })}
    </span>
  );
}

/** Главные переводы карточек — заголовок слова («рисовать, тянуть»). */
export function checkedTranslations(senses: WordSense[], ids: string[]): string {
  return senses
    .filter((sense) => !sense.isRare && ids.includes(sense.id))
    .map((sense) => sense.translations[0]?.text)
    .filter(Boolean)
    .join(', ');
}

interface SessionSensesProps {
  /** Изучаемое слово — выделяется в примерах. */
  word: string;
  /** Смыслы значения, включая «редкие». */
  senses: WordSense[];
  /** Смыслы, ставшие карточками. */
  cardIds: string[];
  /** Удалить карточку; последняя удаляет всё слово. Нет — только просмотр. */
  onRemove?: (senseId: string) => void;
  /** tile — раскрытая плитка очереди создания (desktop); panel — карточка модуля
   *  и шторка модуля; page — мобильная страница «Карточка готова». */
  variant: 'tile' | 'panel' | 'page';
  /** panel: определение под примером. */
  showDefinition?: boolean;
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden>
      <path d="M3 4h10M6.5 4V2.5h3V4M5 4l.5 9a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1L11 4M7 7v4M9 7v4" />
    </svg>
  );
}

/**
 * Карточки слова в одной части речи (2026-09-25): каждый выбранный перевод — отдельная
 * карточка. После создания карточку можно только удалить — первый клик по корзине
 * просит подтверждения. Переводы, которые не стали карточками (включая «редкие» из
 * словаря), — свёрнутым блоком внизу, только в карточке модуля (`panel`): на экране
 * создания его нет (решение владельца 2026-10-06).
 */
export function SessionSenses({
  word,
  senses: allSenses,
  cardIds,
  onRemove,
  variant,
  showDefinition = true,
}: SessionSensesProps) {
  const { t } = useTranslation();
  const { senses: regular, rare } = splitRareSenses(allSenses);
  const [showAll, setShowAll] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);
  const [armed, setArmed] = useState<string | null>(null);
  const armTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (armTimer.current) window.clearTimeout(armTimer.current);
  }, []);

  const cards = regular.filter((sense) => cardIds.includes(sense.id));
  const cardTexts = new Set(
    cards.flatMap((sense) => sense.translations.map((tr) => tr.text.trim().toLowerCase())),
  );
  const other = [
    ...new Set(
      [
        ...regular
          .filter((sense) => !cardIds.includes(sense.id))
          .flatMap((sense) => sense.translations.map((tr) => tr.text.trim())),
        ...rare.map((text) => text.trim()),
      ].filter((text) => text && !cardTexts.has(text.toLowerCase())),
    ),
  ];
  // Чипы «Не выбранных переводов» тянутся на всю строку, если она заполнена хотя бы
  // наполовину; одиночный чип и полупустая строка — своей ширины.
  const otherChipsRef = useRef<HTMLDivElement>(null);
  useLineFill(otherChipsRef, [other.join('|'), otherOpen]);
  const tile = variant === 'tile';
  const visible = tile && !showAll ? cards.slice(0, TILE_VISIBLE) : cards;
  const hidden = cards.length - TILE_VISIBLE;
  const last = cards.length === 1;

  const clickRemove = (senseId: string) => {
    if (!onRemove) return;
    if (armTimer.current) window.clearTimeout(armTimer.current);
    if (armed === senseId) {
      setArmed(null);
      onRemove(senseId);
      return;
    }
    setArmed(senseId);
    armTimer.current = window.setTimeout(() => setArmed(null), CONFIRM_MS);
  };

  return (
    <div className={cn('ss', tile ? 'ss--tile' : 'ss--panel', variant === 'page' && 'ss--page')}>
      <div className="ss-head">
        <span className="ss-eyebrow">{t('sessionSenses.title', { total: cards.length })}</span>
      </div>

      <div className="ss-list">
        {visible.map((sense) => {
          // Только выбранный перевод карточки: синонимы его группы ученик не выбирал (владелец
          // 2026-10-06). В тренировке они по-прежнему засчитываются ответом.
          const [main] = sense.translations;
          // Пара «предложение — перевод» (владелец 2026-10-07): у простого предложения перевод
          // хранится, у example_en — нет. Без перевода (старые, ручные) — прежний пример.
          const pairRu = sense.simpleExampleEn && sense.simpleExampleRu ? sense.simpleExampleRu : null;
          const sentence = pairRu ? sense.simpleExampleEn : sense.exampleEn;
          const example = sentence ? splitExample(sentence, word) : null;
          // Другие употребления того же перевода (close: «близко к парку», «близкий друг»).
          const contexts = (sense.contexts ?? [])
            .map((context) => context.exampleEn)
            .filter((text): text is string => Boolean(text))
            .map((text) => splitExample(text, word));
          const isArmed = armed === sense.id;
          return (
            <div key={sense.id} className="ss-row">
              <div className="ss-body">
                <div className="ss-tr">
                  <span className="ss-tr-main">{main?.text}</span>
                </div>
                {example && (
                  <div className={cn(pairRu && 'ss-pair')}>
                    <div className="ss-example" lang="en">
                      {example.pre}
                      {example.word && <b>{example.word}</b>}
                      {example.post}
                    </div>
                    {pairRu && (
                      <div className="ss-example-ru" lang="ru">
                        {pairRu}
                      </div>
                    )}
                  </div>
                )}
                {contexts.map((context, index) => (
                  <div key={index} className="ss-example ss-example--extra" lang="en">
                    {context.pre}
                    {context.word && <b>{context.word}</b>}
                    {context.post}
                  </div>
                ))}
                {sense.definition && (tile || showDefinition) && (
                  // Подпись «Смысл»: без неё строка под примером читалась как его перевод
                  // (владелец 2026-10-06). В тренировке смысл показывается после ответа.
                  <div className="ss-def" title={t('sessionSenses.definitionTitle')}>
                    <span className="ss-def-label">{t('sessionSenses.definitionLabel')}</span> {sense.definition}
                  </div>
                )}
              </div>
              {onRemove && (
                <button
                  type="button"
                  className={cn('ss-del', isArmed && 'is-armed')}
                  title={last ? t('sessionSenses.removeLast') : t('sessionSenses.remove')}
                  aria-label={last ? t('sessionSenses.removeLast') : t('sessionSenses.remove')}
                  onClick={() => clickRemove(sense.id)}
                >
                  <TrashIcon />
                  {isArmed && (
                    <span>{last ? t('sessionSenses.removeWordConfirm') : t('sessionSenses.removeConfirm')}</span>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {tile && hidden > 0 && (
        <button type="button" className="ss-more" onClick={() => setShowAll((v) => !v)}>
          <svg viewBox="0 0 24 24" className={cn(showAll && 'is-open')} aria-hidden>
            <path d="M6 9l6 6 6-6" />
          </svg>
          {showAll ? t('sessionSenses.collapse') : t('sessionSenses.more', { count: hidden })}
        </button>
      )}

      {other.length > 0 && variant === 'panel' && (
        <div className="ss-other ss-other--panel">
          <button
            type="button"
            className="ss-other-toggle"
            aria-expanded={otherOpen}
            onClick={() => setOtherOpen((v) => !v)}
          >
            <span className="ss-eyebrow">{t('sessionSenses.otherTitle', { count: other.length })}</span>
            <svg viewBox="0 0 24 24" className={cn(otherOpen && 'is-open')} aria-hidden>
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {otherOpen ? (
            <>
              <div ref={otherChipsRef} className="ss-other-chips">
                {other.map((text) => (
                  <span key={text} className="ss-chip">
                    {text}
                  </span>
                ))}
              </div>
              <div className="ss-other-note">{t('sessionSenses.otherNotePanel')}</div>
            </>
          ) : (
            <div className="ss-other-preview">
              {other.slice(0, PREVIEW_OTHER).join(', ')}
              {other.length > PREVIEW_OTHER ? '…' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
